// ═══════════════════════════════════════════════════════════════════════════════
// Subscription billing period calculation (§22.9.4.1)
// ═══════════════════════════════════════════════════════════════════════════════
import { SUBSCRIPTION_BILLING_MODE, } from '../types/subscription.js';
import { SUBSCRIPTION_BILLING_INTERVAL, } from './subscriptionPromotions.js';
/** Stripe requires subscription `trial_end` to be at least 2 days after creation. */
export const STRIPE_MIN_TRIAL_MS = 2 * 24 * 60 * 60 * 1000;
const formatUtcDatetime = (date) => {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
};
/** Add one billing period in UTC (short months follow JS Date / Stripe behaviour). */
export const addBillingPeriodUtc = (date, interval = SUBSCRIPTION_BILLING_INTERVAL.MONTH) => {
    const result = new Date(date.getTime());
    if (interval === SUBSCRIPTION_BILLING_INTERVAL.YEAR) {
        result.setUTCFullYear(result.getUTCFullYear() + 1);
        return result;
    }
    if (interval !== SUBSCRIPTION_BILLING_INTERVAL.MONTH) {
        throw new Error(`Unsupported billing interval: ${interval}`);
    }
    result.setUTCMonth(result.getUTCMonth() + 1);
    return result;
};
/**
 * Free gap before first charge is only usable with Stripe `trial_end` when it is
 * at least {@link STRIPE_MIN_TRIAL_MS} after subscribeAt; otherwise charge today.
 */
export const resolveFirstPaidPeriodStart = (subscribeAt, candidateEndsAt) => {
    if (!candidateEndsAt) {
        return new Date(subscribeAt.getTime());
    }
    const gapMs = candidateEndsAt.getTime() - subscribeAt.getTime();
    if (gapMs <= 0) {
        return new Date(subscribeAt.getTime());
    }
    if (gapMs < STRIPE_MIN_TRIAL_MS) {
        return new Date(subscribeAt.getTime());
    }
    return new Date(candidateEndsAt.getTime());
};
export const calcPrepayBillingPeriod = (input) => {
    const billingInterval = input.billingInterval ?? SUBSCRIPTION_BILLING_INTERVAL.MONTH;
    const subscribeAt = new Date(input.subscribeAt.getTime());
    const invitationEndsAt = input.invitationLevelEndsAt
        ? new Date(input.invitationLevelEndsAt.getTime())
        : null;
    const candidateEndsAt = invitationEndsAt && invitationEndsAt.getTime() > subscribeAt.getTime()
        ? invitationEndsAt
        : null;
    const firstPaidPeriodStart = resolveFirstPaidPeriodStart(subscribeAt, candidateEndsAt);
    const firstPaidPeriodEnd = addBillingPeriodUtc(firstPaidPeriodStart, billingInterval);
    const billingCycleAnchor = firstPaidPeriodEnd;
    const billingMode = SUBSCRIPTION_BILLING_MODE.PREPAY_THEN_ANCHOR;
    const nextChargeAt = firstPaidPeriodStart.getTime() > subscribeAt.getTime() ? firstPaidPeriodStart : billingCycleAnchor;
    return {
        billingMode,
        billingInterval,
        subscribeAt: formatUtcDatetime(subscribeAt),
        firstPaidPeriodStart: formatUtcDatetime(firstPaidPeriodStart),
        firstPaidPeriodEnd: formatUtcDatetime(firstPaidPeriodEnd),
        billingCycleAnchor: formatUtcDatetime(billingCycleAnchor),
        nextChargeAt: formatUtcDatetime(nextChargeAt),
    };
};
export const parseUtcDatetimeToDate = (value) => {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/);
    if (!match) {
        throw new Error(`Invalid UTC datetime: ${value}`);
    }
    const [, year, month, day, hour, minute] = match;
    return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), 0, 0));
};
//# sourceMappingURL=billing.js.map